import {
    formatAddress,
    formatDate,
    formatNameLastFirst,
    formatPhoneNumber,
} from '@strata/helper-functions';
import { ActionButtonPageTitle, Button } from '@strata/react-buttons';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FormAccordion } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { EditPatientPhoneNumberDialog } from 'components/intakeForm/dialogs/EditPatientPhoneNumberDialog';
import { ResetFormDialog } from 'components/intakeForm/dialogs/ResetFormDialog';
import { HealthPlanAndPatient } from 'components/intakeForm/HealthPlanAndPatient';
import { Patient } from 'components/intakeForm/Patient';
import { PointOfContact } from 'components/intakeForm/PointOfContact';
import { Prescriber } from 'components/intakeForm/Prescriber';
import { Product } from 'components/intakeForm/Product';
import {
    type SaveRequiredFields,
    SectionCompletionStatus,
} from 'components/intakeForm/SectionCompletionStatus';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { usePostSaveIntakeCaseMutation } from 'store/api/intakeCaseApi';
import { useGetSegmentsQuery } from 'store/api/referenceApi';
import { FormSections, FormSectionTitleMap } from 'store/intakeForm/intakeFormSchema';
import { closeSection, openSection, resetForm } from 'store/intakeForm/intakeFormThunks';

const FormSectionComponentMap = {
    [FormSections.POINT_OF_CONTACT]: PointOfContact,
    [FormSections.HEALTH_PLAN_AND_PATIENT]: HealthPlanAndPatient,
    [FormSections.PRODUCT]: Product,
    [FormSections.PRESCRIBER]: Prescriber,
    [FormSections.PATIENT]: Patient,
} as const;

export const IntakeForm = () => {
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const { sections, capturedData } = useAppSelector((state) => state.intakeForm);

    const [postSaveIntakeCase] = usePostSaveIntakeCaseMutation();

    const { data: segments } = useGetSegmentsQuery();

    /** Don't show header until Health Plan & Patient section is completed */
    const showHeader =
        sections[FormSections.HEALTH_PLAN_AND_PATIENT].progressIndicatorStatus ===
        FormAccordion.statuses.COMPLETED;

    /** First two sections must be completed before saving intake case */
    const canSaveIntakeCase =
        sections[FormSections.POINT_OF_CONTACT].progressIndicatorStatus ===
            FormAccordion.statuses.COMPLETED &&
        sections[FormSections.HEALTH_PLAN_AND_PATIENT].progressIndicatorStatus ===
            FormAccordion.statuses.COMPLETED;

    const requiredFields: SaveRequiredFields = {
        intakeSource: capturedData[FormSections.POINT_OF_CONTACT].intakeSource,
        contactFirstName: capturedData[FormSections.POINT_OF_CONTACT].contactFirstName,
        contactLastName: capturedData[FormSections.POINT_OF_CONTACT].contactLastName,
        contactPhoneNumber: capturedData[FormSections.POINT_OF_CONTACT].contactPhoneNumber,
        contactOfficeName: capturedData[FormSections.POINT_OF_CONTACT].contactOfficeName,
        segmentId: capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].segmentId,
        memberFirstName: capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberFirstName,
        memberLastName: capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberLastName,
        memberDateOfBirth: formatDate(
            capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberDateOfBirth,
        ),
        memberId: capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberId,
        memberGender: capturedData[FormSections.PATIENT].memberGender,
        memberAddress1: capturedData[FormSections.PATIENT].memberAddress1,
        memberCity: capturedData[FormSections.PATIENT].memberCity,
        memberState: capturedData[FormSections.PATIENT].memberState,
        memberZipcode: capturedData[FormSections.PATIENT].memberZipcode,
        benefit: capturedData[FormSections.PRODUCT].benefit,
        productServiceOid: capturedData[FormSections.PRODUCT].productServiceOid,
        productServiceId: capturedData[FormSections.PRODUCT].productServiceId,
        prescriberVerified: capturedData[FormSections.PRESCRIBER].prescriberVerified,
        prescriberNpi: capturedData[FormSections.PRESCRIBER].prescriberNpi,
        prescriberFirstName: capturedData[FormSections.PRESCRIBER].prescriberFirstName,
        prescriberLastName: capturedData[FormSections.PRESCRIBER].prescriberLastName,
        prescriberPhoneNumber: capturedData[FormSections.PRESCRIBER].prescriberPhoneNumber,
        prescriberAddress1: capturedData[FormSections.PRESCRIBER].prescriberAddress1,
        prescriberCity: capturedData[FormSections.PRESCRIBER].prescriberCity,
        prescriberState: capturedData[FormSections.PRESCRIBER].prescriberState,
        prescriberZip: capturedData[FormSections.PRESCRIBER].prescriberZip,
    };

    const intakeFormSections = Object.values(FormSections)
        .filter((section) => section !== FormSections.PATIENT)
        .map((section) => {
            const SectionComponent = FormSectionComponentMap[section];
            const { isOpen, progressIndicatorStatus } = sections[section];
            return {
                sectionTitle: (
                    <span className="flex__gap--medium" style={{ justifyContent: 'space-between' }}>
                        <span>{FormSectionTitleMap[section]}</span>
                        <SectionCompletionStatus section={section} values={requiredFields} />
                    </span>
                ),
                content: <SectionComponent isOpen={isOpen} />,
                step: section,
                progressIndicatorStatus,
                isOpen,
                onSectionClick: () => dispatch((!isOpen ? openSection : closeSection)(section)),
                disabled: progressIndicatorStatus === FormAccordion.statuses.NOT_STARTED,
                scrollToTopOnOpen: true,
                scrollOffset: 44,
            };
        });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const saveIntakeCase = () => {
        void (async () => {
            try {
                setIsSubmitting(true);
                await postSaveIntakeCase(capturedData).unwrap();
                SuccessToast({ message: 'Intake Case saved successfully.' });
                dispatch(resetForm());
            } finally {
                setIsSubmitting(false);
            }
        })();
    };

    return (
        <FormHeaderAndContent
            rowOneContentHeader={
                <div className="flex__gap--small" style={{ width: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                        <ActionButtonPageTitle
                            label="Active Intake Cases"
                            id="active-intake-cases-page-title-button"
                            title="Intake Form"
                            onClick={() => navigate(AppRoutes.activeIntakeCases)}
                        />
                    </div>
                    <div className="flex__gap--small" style={{ flexShrink: 0 }}>
                        <ResetFormDialog />
                        <Button
                            label="Save"
                            id="save-intake-case-button"
                            intent={Button.Intent.PRIMARY}
                            disabled={!canSaveIntakeCase || isSubmitting}
                            onClick={saveIntakeCase}
                        />
                    </div>
                </div>
            }
            rowTwoContentHeader={
                showHeader && (
                    <>
                        <FormTitleLabelValuePair
                            label={formatNameLastFirst(
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberLastName,
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberFirstName,
                            )}
                            value={formatAddress(
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberCity,
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberState,
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberZip,
                            )}
                        />
                        <HeaderLabelValuePair
                            label="Health Plan"
                            value={
                                segments?.find(
                                    ({ segmentId }) =>
                                        segmentId ===
                                        capturedData[FormSections.HEALTH_PLAN_AND_PATIENT]
                                            .segmentId,
                                )?.name
                            }
                        />
                        <HeaderLabelValuePair
                            label="Patient Date Of Birth"
                            value={formatDate(
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT]
                                    .memberDateOfBirth,
                            )}
                        />
                        <HeaderLabelValuePair
                            label="Member ID"
                            value={capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberId}
                        />
                        <HeaderLabelValuePair
                            showLabelTooltip={false}
                            label={
                                <div
                                    className="flex__gap--small"
                                    style={{ alignItems: 'flex-end' }}
                                >
                                    <span>Patient Phone Number</span>
                                    <EditPatientPhoneNumberDialog />
                                </div>
                            }
                            value={formatPhoneNumber(
                                capturedData[FormSections.HEALTH_PLAN_AND_PATIENT]
                                    .memberPhoneNumber,
                            )}
                        />
                    </>
                )
            }
            rowThreeContentHeader={
                showHeader &&
                (() => {
                    if (!capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberVerified) {
                        return (
                            <InlineMessage
                                intent={InlineMessage.Intent.ERROR}
                                label="This patient is unverified."
                            />
                        );
                    }
                    if (!capturedData[FormSections.HEALTH_PLAN_AND_PATIENT].memberEligible) {
                        return (
                            <InlineMessage
                                intent={InlineMessage.Intent.WARNING}
                                label="This patient failed benefit check eligibility."
                            />
                        );
                    }
                    return null;
                })()
            }
            mainContent={<FormAccordion sectionDataArray={intakeFormSections} />}
        />
    );
};
